import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardHeader,
  ErrorState,
  PageHeader,
  PhotoTile,
  Skeleton,
  Table,
  TableWrap,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useApi } from "@/lib/api";
import { ago, count, date, decimal, personLabel } from "@/lib/format";

/**
 * One person.
 *
 * The two operator actions here are deliberately mild. Marking an account
 * internal changes nothing about what it can do — it only takes the account
 * out of every dashboard figure, which is what our own logins need.
 * Deactivating starts the same seven-day clock `DELETE /me` starts, and it
 * is reversible for exactly as long as that clock runs.
 *
 * There is no way to erase somebody from here. That is the purge job's
 * business, and an operator who could destroy a GDPR grace window with one
 * click is a mistake waiting for a bad afternoon.
 */
export function UserDetailScreen() {
  const { userId = "" } = useParams();
  const api = useApi();
  const queryClient = useQueryClient();

  const user = useQuery({
    queryKey: ["admin", "user", userId],
    queryFn: async () => {
      const { data, error } = await api.GET("/admin/users/{userId}", {
        params: { path: { userId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["admin", "user", userId] });
    void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
  };

  const setInternal = useMutation({
    mutationFn: async (isInternal: boolean) => {
      const { error } = await api.PATCH("/admin/users/{userId}", {
        params: { path: { userId } },
        body: { isInternal },
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const deactivate = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/admin/users/{userId}/deactivate", {
        params: { path: { userId } },
        body: {},
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const restore = useMutation({
    mutationFn: async () => {
      const { error } = await api.POST("/admin/users/{userId}/restore", {
        params: { path: { userId } },
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  if (user.isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader eyebrow="People" title="Person" />
        <ErrorState onRetry={() => void user.refetch()} />
      </div>
    );
  }

  const d = user.data;
  const busy =
    setInternal.isPending || deactivate.isPending || restore.isPending;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        eyebrow="People"
        title={d ? personLabel(d.user) : "…"}
        description={d?.user.email}
        actions={
          d ? (
            <>
              <Button
                disabled={busy}
                onClick={() => setInternal.mutate(!d.user.isInternal)}
              >
                {d.user.isInternal ? "Not internal" : "Mark internal"}
              </Button>
              {d.user.deletedAt ? (
                <Button
                  variant="primary"
                  disabled={busy}
                  onClick={() => restore.mutate()}
                >
                  Restore
                </Button>
              ) : (
                <Button
                  variant="danger"
                  disabled={busy}
                  onClick={() => deactivate.mutate()}
                >
                  Deactivate
                </Button>
              )}
            </>
          ) : null
        }
      />

      {!d ? (
        <Skeleton className="h-96" />
      ) : (
        <>
          {d.user.deletedAt ? (
            <p className="rounded-panel border border-danger/25 bg-danger-soft px-3 py-2 text-[12px] text-danger">
              Deactivated {ago(d.user.deletedAt)}. Invisible in the product, and
              the purge job erases it seven days after that stamp. Restoring
              before then gives everything back.
            </p>
          ) : null}

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
            <StatTile label="Reviews" value={d.user.reviewCount} />
            <StatTile label="Check-ins" value={d.user.checkInCount} />
            <StatTile label="Photos" value={d.user.photoCount} />
            <StatTile label="Reports filed" value={d.reportsFiled} />
            <StatTile
              label="Reports against them"
              value={d.reportsReceived}
              alarming={d.reportsReceived > 0}
            />
          </section>

          <section className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            <Card>
              <CardHeader
                title="Profile"
                subtitle={
                  d.profile
                    ? "What they chose to say about themselves."
                    : "No profile — they have not claimed a handle."
                }
              />
              {d.profile ? (
                <dl className="flex flex-col gap-2">
                  <Row label="Handle" value={d.user.handle ?? "—"} />
                  <Row label="Name" value={d.user.displayName ?? "—"} />
                  <Row label="Tagline" value={d.profile.tagline ?? "—"} />
                  <Row label="Bio" value={d.profile.bio ?? "—"} />
                  <Row label="Home" value={d.profile.homeCity ?? "—"} />
                  <Row
                    label="Favourite"
                    value={
                      d.profile.favouriteCafe ? (
                        <Link
                          to={`/cafes/${d.profile.favouriteCafe.id}`}
                          className="text-accent hover:underline"
                        >
                          {d.profile.favouriteCafe.name}
                        </Link>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <Row
                    label="Usually"
                    value={
                      [
                        d.profile.usualDrink,
                        d.profile.usualMilk,
                        d.profile.sweetness,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "—"
                    }
                  />
                  <Row label="Grade" value={d.profile.gradePreference ?? "—"} />
                  <Row
                    label="Drinking since"
                    value={d.profile.matchaSince?.toString() ?? "—"}
                  />
                  <Row
                    label="Instagram"
                    value={
                      d.profile.instagramHandle ? (
                        <span className="flex items-center gap-1.5">
                          @{d.profile.instagramHandle}
                          {d.profile.instagramVerified ? (
                            <Badge tone="success">email confirmed</Badge>
                          ) : (
                            <Badge tone="neutral">email not confirmed</Badge>
                          )}
                        </span>
                      ) : (
                        "—"
                      )
                    }
                  />
                  <Row
                    label="Reputation"
                    value={
                      <span className="font-mono">
                        {decimal(d.profile.reputation, 2)}
                      </span>
                    }
                  />
                  <Row label="Joined" value={date(d.user.createdAt)} />
                </dl>
              ) : (
                <dl className="flex flex-col gap-2">
                  <Row label="Joined" value={date(d.user.createdAt)} />
                </dl>
              )}
            </Card>

            <div className="flex flex-col gap-3">
              <Card padded={false}>
                <div className="px-4 pt-4">
                  <CardHeader title="Recent reviews" />
                </div>
                {d.recentReviews.length === 0 ? (
                  <p className="px-4 pb-5 text-micro text-muted">
                    They have not reviewed anything.
                  </p>
                ) : (
                  <TableWrap>
                    <Table>
                      <THead>
                        <TR>
                          <TH>Note</TH>
                          <TH>Cafe</TH>
                          <TH align="right">Matcha</TH>
                          <TH>Posted</TH>
                        </TR>
                      </THead>
                      <tbody>
                        {d.recentReviews.map((review) => (
                          <TR key={review.id}>
                            <TD className="max-w-[300px]">
                              <Link
                                to={`/reviews/${review.id}`}
                                className="block truncate text-ink-2 hover:text-accent hover:underline"
                              >
                                {review.note ?? "no note"}
                              </Link>
                            </TD>
                            <TD muted>{review.cafe?.name ?? "—"}</TD>
                            <TD align="right" mono>
                              {decimal(review.rating)}
                            </TD>
                            <TD muted>{ago(review.createdAt)}</TD>
                          </TR>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                )}
              </Card>

              {d.recentCheckIns.length > 0 ? (
                <Card padded={false}>
                  <div className="px-4 pt-4">
                    <CardHeader title="Recent check-ins" />
                  </div>
                  <TableWrap>
                    <Table>
                      <THead>
                        <TR>
                          <TH>Cafe</TH>
                          <TH>Local date</TH>
                          <TH>Review</TH>
                          <TH>When</TH>
                        </TR>
                      </THead>
                      <tbody>
                        {d.recentCheckIns.map((checkIn) => (
                          <TR key={checkIn.id}>
                            <TD>
                              {checkIn.cafe ? (
                                <Link
                                  to={`/cafes/${checkIn.cafe.id}`}
                                  className="text-ink-2 hover:text-accent hover:underline"
                                >
                                  {checkIn.cafe.name}
                                </Link>
                              ) : (
                                "—"
                              )}
                            </TD>
                            <TD mono muted>
                              {checkIn.localDate}
                            </TD>
                            <TD>
                              {checkIn.hasReview ? (
                                <Badge tone="accent">reviewed</Badge>
                              ) : (
                                <span className="text-muted-2">—</span>
                              )}
                            </TD>
                            <TD muted>{ago(checkIn.createdAt)}</TD>
                          </TR>
                        ))}
                      </tbody>
                    </Table>
                  </TableWrap>
                </Card>
              ) : null}

              {d.photos.length > 0 ? (
                <Card>
                  <CardHeader
                    title="Photos"
                    subtitle={`${count(d.photos.length)} uploaded`}
                  />
                  <div className="flex flex-wrap gap-2">
                    {d.photos.map((photo) => (
                      <PhotoTile key={photo.id} photo={photo} />
                    ))}
                  </div>
                </Card>
              ) : null}
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function StatTile({
  label,
  value,
  alarming,
}: {
  label: string;
  value: number;
  alarming?: boolean;
}) {
  return (
    <div className="rounded-panel border border-border bg-surface p-3">
      <p className="eyebrow">{label}</p>
      <p
        className={
          alarming
            ? "mt-1 font-mono text-stat text-danger"
            : "mt-1 font-mono text-stat text-ink"
        }
      >
        {count(value)}
      </p>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-[104px] shrink-0 text-micro text-muted">{label}</dt>
      <dd className="min-w-0 text-[12px] text-ink-2">{value}</dd>
    </div>
  );
}
